import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { SegmentService } from '../../segments/services/segment-api.service';
import { SegmentMemberResponse } from '../../segments/models/segment.model';

/**
 * Modal para ver los contactos que componen un segmento, con búsqueda
 * (por nombre/email, contra el backend) y scroll infinito paginado.
 */
@Component({
  selector: 'app-segment-members-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './segment-members-modal.component.html',
})
export class SegmentMembersModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() segmentId: number | null = null;
  @Input() segmentName = '';
  @Output() closed = new EventEmitter<void>();

  members: SegmentMemberResponse[] = [];
  loading = false;
  loadingMore = false;
  totalElements = 0;
  searchTerm = '';

  private page = 0;
  private readonly size = 30;
  private hasNext = false;

  private readonly search$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(private segmentApi: SegmentService) {
    this.search$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.reload());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue && this.segmentId != null) {
      this.searchTerm = '';
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(): void {
    this.search$.next();
  }

  private reload(): void {
    if (this.segmentId == null) return;
    this.page = 0;
    this.members = [];
    this.hasNext = false;
    this.loading = true;
    this.segmentApi
      .getMembers(this.segmentId, 0, this.size, this.searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.members = res.content ?? [];
          this.totalElements = res.totalElements ?? this.members.length;
          this.hasNext = res.hasNext;
          this.loading = false;
        },
        error: () => {
          this.members = [];
          this.totalElements = 0;
          this.hasNext = false;
          this.loading = false;
        },
      });
  }

  onScroll(ev: Event): void {
    const el = ev.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasNext || this.segmentId == null) return;
    this.loadingMore = true;
    const next = this.page + 1;
    this.segmentApi
      .getMembers(this.segmentId, next, this.size, this.searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.page = next;
          this.members = [...this.members, ...(res.content ?? [])];
          this.hasNext = res.hasNext;
          this.loadingMore = false;
        },
        error: () => {
          this.loadingMore = false;
        },
      });
  }

  close(): void {
    this.closed.emit();
  }

  trackById = (_: number, m: SegmentMemberResponse) => m.id;
}
